import { supabase } from './supabase'
import type { DocKind } from './types'

/**
 * Envoi d'une photo ou d'un PDF vers le seau prive "documents", puis
 * enregistrement d'une ligne dans la table documents.
 *
 * COMPRESSION : les telephones produisent des photos de 3 a 8 Mo. Sur
 * la connexion de Mbour, envoyer un tel fichier prend des minutes et
 * echoue souvent. Chaque image est donc reduite a 1600 px de cote et
 * reencodee en JPEG avant l'envoi, ce qui donne en general moins de
 * 300 Ko pour une facture ou un bordereau parfaitement lisible.
 *
 * Les PDF passent tels quels : on ne peut pas les recompresser dans le
 * navigateur, et un scan de contrat doit rester integral.
 */

const MAX_COTE = 1600
const QUALITE = 0.72

export interface UploadTarget {
  kind: DocKind
  label?: string | null
  unitId?: string | null
  tenantId?: string | null
  period?: string | null
  /** Bail auquel rattacher la piece. Un bail peut en porter plusieurs. */
  leaseId?: string | null
}

export interface UploadResult {
  documentId: string
  storagePath: string
  byteSize: number
}

/** Reduit une image et renvoie un JPEG. Renvoie le fichier tel quel si
 *  ce n'est pas une image ou si la reduction echoue. */
export async function compressImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file

  try {
    const bitmap = await createImageBitmap(file)
    const echelle = Math.min(1, MAX_COTE / Math.max(bitmap.width, bitmap.height))
    const largeur = Math.round(bitmap.width * echelle)
    const hauteur = Math.round(bitmap.height * echelle)

    const canvas = document.createElement('canvas')
    canvas.width = largeur
    canvas.height = hauteur
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, largeur, hauteur)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITE),
    )
    // Si la compression a grossi le fichier, on garde l'original.
    return blob && blob.size < file.size ? blob : file
  } catch {
    return file
  }
}

function extensionPour(blob: Blob, original: File): string {
  if (blob.type === 'image/jpeg') return 'jpg'
  if (original.type === 'application/pdf') return 'pdf'
  const parts = original.name.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'bin'
}

export async function uploadDocument(
  file: File,
  target: UploadTarget,
): Promise<UploadResult> {
  const blob = await compressImage(file)

  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id ?? null

  // Chemin lisible : le type de piece, puis la periode, puis un
  // identifiant unique. On peut retrouver un fichier a la main dans
  // Supabase sans passer par l'application.
  const dossier = target.period ?? 'sans-periode'
  const nom = `${crypto.randomUUID()}.${extensionPour(blob, file)}`
  const storagePath = `${target.kind}/${dossier}/${nom}`

  const { error: upErr } = await supabase.storage
    .from('documents')
    .upload(storagePath, blob, {
      contentType: blob.type || file.type || 'application/octet-stream',
      upsert: false,
    })
  if (upErr) throw upErr

  const { data, error } = await supabase
    .from('documents')
    .insert({
      storage_path: storagePath,
      kind: target.kind,
      label: target.label ?? null,
      unit_id: target.unitId ?? null,
      tenant_id: target.tenantId ?? null,
      period: target.period ?? null,
      lease_id: target.leaseId ?? null,
      uploaded_by: userId,
      byte_size: blob.size,
      mime_type: blob.type || file.type || null,
    })
    .select('id')
    .single()

  if (error) {
    // La ligne n'a pas pu etre creee : on retire le fichier pour ne pas
    // laisser un orphelin dans le seau.
    await supabase.storage.from('documents').remove([storagePath])
    throw error
  }

  return {
    documentId: data.id as string,
    storagePath,
    byteSize: blob.size,
  }
}

/**
 * URL temporaire pour afficher une piece. Le seau est prive : il n'y a
 * pas d'URL publique, chaque lecture est signee pour une heure.
 */
export async function signedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(storagePath, 3600)
  return error ? null : data.signedUrl
}
